import { useState, useEffect } from 'react';
import { BrowserRouter, Routes, Route, Navigate } from 'react-router-dom';
import { useAuthStore } from './stores/auth-store.js';
import { useNativeApp } from './hooks/useNativeApp.js';
import { usePageTracker } from './hooks/usePageTracker.js';
import { api } from './services/api-client.js';
import Layout from './components/Layout.js';
import LandingPage from './pages/LandingPage.js';
import LoginPage from './pages/LoginPage.js';
import OnboardingPage from './pages/OnboardingPage.js';
import DashboardPage from './pages/DashboardPage.js';
import AddVehiclePage from './pages/AddVehiclePage.js';
import InventoryPage from './pages/InventoryPage.js';
import VehicleDetailPage from './pages/VehicleDetailPage.js';
import SettingsPage from './pages/SettingsPage.js';
import BillingPage from './pages/BillingPage.js';
import AdminDashboardPage from './pages/AdminDashboardPage.js';
import MarketplacePage from './pages/MarketplacePage.js';
import CustomersPage from './pages/CustomersPage.js';
import DealsPage from './pages/DealsPage.js';
import DealFlowPage from './pages/DealFlowPage.js';
import WebsiteBuilderPage from './pages/WebsiteBuilderPage.js';
import MobileEntryPage from './pages/MobileEntryPage.js';
import BrowsePage from './pages/BrowsePage.js';
import ListingPage from './pages/ListingPage.js';
import TermsPage from './pages/TermsPage.js';
import PrivacyPage from './pages/PrivacyPage.js';
import AcceptableUsePage from './pages/AcceptableUsePage.js';
import NotFoundPage from './pages/NotFoundPage.js';
import AuctionScanPage from './pages/AuctionScanPage.js';
import QuickListPage from './pages/QuickListPage.js';
import ConnectAccountsPage from './pages/ConnectAccountsPage.js';
import OAuthCallbackPage from './pages/OAuthCallbackPage.js';

// Shows landing if not logged in, dashboard if logged in
function RootRoute() {
  return <LandingPage />;
}

function ProtectedRoute({ children }: { children: React.ReactNode }) {
  const token = useAuthStore((s) => s.token);
  if (!token) return <Navigate to="/" replace />;
  return <>{children}</>;
}

function AdminRoute({ children }: { children: React.ReactNode }) {
  const token = useAuthStore((s) => s.token);
  const user = useAuthStore((s) => s.user);
  if (!token) return <Navigate to="/" replace />;
  if (user?.role !== 'admin' && user?.role !== 'owner') return <Navigate to="/dashboard" replace />;
  return <>{children}</>;
}

function DealerRoute({ children }: { children: React.ReactNode }) {
  const isDealer = useAuthStore((s) => s.isDealer);
  if (!isDealer()) return <Navigate to="/dashboard" replace />;
  return <>{children}</>;
}

function PublicOnly({ children }: { children: React.ReactNode }) {
  const token = useAuthStore((s) => s.token);
  if (token) return <Navigate to="/dashboard" replace />;
  return <>{children}</>;
}

export default function App() {
  useNativeApp();
  usePageTracker();
  const token = useAuthStore((s) => s.token);
  const [ready, setReady] = useState(!token);

  useEffect(() => {
    const store = useAuthStore.getState();

    if (token) {
      // Validate existing session
      api.get('/auth/me').then((res) => {
        if (!res.success && (res.code === 'UNAUTHORIZED' || res.code === 'NOT_FOUND')) {
          store.logout();
        }
      }).catch(() => {}).finally(() => setReady(true));
      return;
    }

    // No token — try device trust auto-login
    const deviceToken = store.deviceToken;
    if (deviceToken) {
      api.post<{ accounts: any[] }>('/auth/device-login', { device_token: deviceToken })
        .then((res) => {
          if (res.success && res.data?.accounts?.length) {
            store.setAccounts(res.data.accounts);
          } else {
            // Device token expired — clear it
            store.logout();
          }
        }).catch(() => {}).finally(() => setReady(true));
      return;
    }

    setReady(true);
  }, []);

  if (!ready) return <div className="min-h-screen bg-surface-900" />;

  return (
    <BrowserRouter>
      <Routes>
        {/* Root — landing page or dashboard */}
        <Route path="/" element={<RootRoute />} />

        {/* Public routes */}
        <Route path="/m" element={<MobileEntryPage />} />
        <Route path="/go" element={<QuickListPage />} />
        <Route path="/login" element={<PublicOnly><LoginPage /></PublicOnly>} />
        <Route path="/browse" element={<BrowsePage />} />
        <Route path="/browse/:id" element={<ListingPage />} />
        <Route path="/sell" element={<Navigate to="/go" replace />} />
        <Route path="/terms" element={<TermsPage />} />
        <Route path="/privacy" element={<PrivacyPage />} />
        <Route path="/acceptable-use" element={<AcceptableUsePage />} />
          <Route path="/404" element={<NotFoundPage />} />

        {/* OAuth callback — popup, auth required, no Layout */}
        <Route path="/oauth/facebook/callback" element={
          <ProtectedRoute><OAuthCallbackPage /></ProtectedRoute>
        } />

        {/* Onboarding — requires auth */}
        <Route path="/onboarding" element={
          <ProtectedRoute><OnboardingPage /></ProtectedRoute>
        } />

        {/* App routes — require auth */}
        <Route
          path="/*"
          element={
            <ProtectedRoute>
              <Layout>
                <Routes>
                  <Route path="/dashboard" element={<DashboardPage />} />
                  <Route path="/add" element={<AddVehiclePage />} />
                  <Route path="/auction" element={<AuctionScanPage />} />
                  <Route path="/inventory" element={<InventoryPage />} />
                  <Route path="/vehicles/:id" element={<VehicleDetailPage />} />
                  <Route path="/settings" element={<SettingsPage />} />
                  <Route path="/connect" element={<ConnectAccountsPage />} />
                  <Route path="/billing" element={<DealerRoute><BillingPage /></DealerRoute>} />
                  <Route path="/marketplace" element={<MarketplacePage />} />
                  <Route path="/customers" element={<DealerRoute><CustomersPage /></DealerRoute>} />
                  <Route path="/deals" element={<DealerRoute><DealsPage /></DealerRoute>} />
                  <Route path="/deals/:id" element={<DealerRoute><DealFlowPage /></DealerRoute>} />
                  <Route path="/website" element={<DealerRoute><WebsiteBuilderPage /></DealerRoute>} />
                  <Route path="/admin" element={<AdminRoute><AdminDashboardPage /></AdminRoute>} />
                  <Route path="*" element={<NotFoundPage />} />
                </Routes>
              </Layout>
            </ProtectedRoute>
          }
        />
      </Routes>
    </BrowserRouter>
  );
}
